import React, { useState, useEffect } from 'react';

import printJS from 'print-js';
import KeyStore from 'utils/KeyStore';
import ModalUtils from 'utils/ModalUtils';
import PayrollUtils from 'utils/PayrollUtils';
import Select from 'components/widgets/Select';
import { ReportName } from 'utils/enum/PayrollEnum';
import DateUtils, { DateFormat } from 'lib/DateUtils';
import { useMutation, useQuery } from '@apollo/client';
import { ALL_LOTS, FetchPolicy } from 'utils/enum/Core';
import RT6Form from 'components/widgets/payroll/PayrollRT6FormDialog';
import NachaEffectiveDate from 'components/widgets/payroll/NachaEffectiveDate';
import PayrollGeneralQuery from 'services/graphQL/query/payroll/PayrollGeneral';
import GeneratedChecksMutation from 'services/graphQL/mutate/payroll/GeneratedChecks';

import {
    Grid,
    List,
    ListItem,
    makeStyles,
    ListItemText,
} from '@material-ui/core';

const REPORTS = [
    { label: 'Payroll Report', value: ReportName.PAYROLL_REPORT },
    { label: 'Deductions Report', value: ReportName.DEDUCTIONS_REPORT },
    { label: 'Additions Report', value: ReportName.ADDITIONS_REPORT },
    { label: 'NACHA File', value: null },
    { label: 'Form RT-6 File', value: null },
];

const ownStyles = makeStyles((theme) => ({
    left: {
        minWidth: 'fit-content',
    },
    listStyle: {
        marginTop: 51,
        padding: 5,
        overflow: 'auto',
        height: 'calc(93vh - 100px)',
        border: `1px solid ${theme.palette.border.primary}`,
    },
    payPeriodList: {
        margin: 10,
        padding: 5,
        overflow: 'auto',
        height: 'calc(93vh - 100px)',
        border: `1px solid ${theme.palette.border.primary}`,
    },
    listItemStyle: {
        padding: '0px 5px 0px',
    },
    listItemRoot: {
        '&$selected': {
            backgroundColor: theme.palette.background.blueRibbon,
            '&:hover': {
                backgroundColor: theme.palette.background.blueRibbon,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.background.blueRibbon,
        },
    },
    yearDropdown: {
        margin: '10px 10px 0px 10px',
    },
    selected: {},
}));

const Reports = () => {
    const classes = { ...ownStyles() };
    const keyStore = new KeyStore();
    const [state, setState] = useState({
        payPeriods: [],
        printing: false,
        reportSelected: -1,
        payPeriodSelected: 0,
        openNachaDialog: false,
        openRT6FormDialog: false,
        payPeriodYear: new Date().getFullYear(),
    });

    const {
        payPeriodYear, payPeriodSelected, openNachaDialog,
        reportSelected, payPeriods, printing, openRT6FormDialog,
    } = state;

    const {
        data: payPeriodsData,
        loading: loadingPayPeriods,
        error: payPeriodsErrors,
    } = useQuery(PayrollGeneralQuery.GET_PAY_PERIODS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        variables: {
            lotName: keyStore.getSelectedLot()?.lotName || ALL_LOTS,
            year: state.payPeriodYear,
        },
    });

    useEffect(() => {
        if (payPeriodsErrors) {
            ModalUtils.errorMessage(payPeriodsErrors?.graphQLErrors);
            return;
        }

        if (!loadingPayPeriods) {
            const { getPayPeriods } = payPeriodsData;
            const currentPayPeriodIndex = getPayPeriods.findIndex((item) => item.includes('(Current)'));
            const selectedPayPeriodIndex = currentPayPeriodIndex >= 1 ? currentPayPeriodIndex - 1 : 0;

            setState((prevState) => ({
                ...prevState,
                payPeriods: getPayPeriods,
                payPeriodSelected: selectedPayPeriodIndex,
            }));
        }
    }, [payPeriodsData, loadingPayPeriods, payPeriodsErrors]);

    const togglePrintDialog = (isPrinting = false) => {
        setState((prevState) => ({
            ...prevState,
            printing: isPrinting,
        }));
    };

    const [generateNachaFile] = useMutation(GeneratedChecksMutation.GENERATE_NACHA_FILE, {
        onCompleted: (mutationData) => {
            if (mutationData?.generateNachaFile) {
                window.open(mutationData?.generateNachaFile);
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
        },
    });

    const [generateRT6FormFile] = useMutation(GeneratedChecksMutation.GENERATE_RT6_FORM_FILE, {
        onCompleted: (mutationData) => {
            if (mutationData?.generateRT6FormFile) {
                window.open(mutationData?.generateRT6FormFile);
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
        },
    });

    const [printPayrollReport] = useMutation(GeneratedChecksMutation.PRINT_PAYROLL_REPORT_BY_NAME_AND_PAY_PERIOD, {
        onCompleted: (mutationData) => {
            if (mutationData?.printPayrollReportByNameAndPayPeriod) {
                printJS({
                    printable: mutationData?.printPayrollReportByNameAndPayPeriod,
                    type: 'pdf',
                    showModal: false,
                    onPrintDialogClose: togglePrintDialog,
                });
            } else {
                // No printable data returned; reset printing state
                togglePrintDialog(false);
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
            // Error occurred; reset printing state
            togglePrintDialog(false);
        },
    });

    const onPrintReport = (formName) => {
        togglePrintDialog(true);
        printPayrollReport({
            variables: {
                formName,
                payPeriod: payPeriods[payPeriodSelected]?.replace(' (Current)', ''),
            },
        });
    };

    const toggleNachaEffectiveDateDialog = (date = null) => {
        if (date) {
            generateNachaFile({
                variables: {
                    effectiveDate: DateUtils.format(date, DateFormat.SHORT_DATE),
                    payPeriod: payPeriods[payPeriodSelected]?.replace(' (Current)', ''),
                },
            });
        }

        setState((prevState) => ({
            ...prevState,
            openNachaDialog: !prevState.openNachaDialog,
        }));
    };

    const toggleRT6FormDialog = (year, quarter) => {
        if (year && quarter) {
            generateRT6FormFile({
                variables: {
                    year,
                    quarter,
                },
            });
        }

        setState((prevState) => ({
            ...prevState,
            openRT6FormDialog: !prevState.openRT6FormDialog,
        }));
    };

    const onChangeReport = (index, reportName) => {
        if (reportName) {
            onPrintReport(reportName);
        } else if (index === 3) {
            toggleNachaEffectiveDateDialog();
        } else if (index === 4) {
            toggleRT6FormDialog();
        }

        setState((prevState) => ({
            ...prevState,
            reportSelected: index,
        }));
    };

    const onChangeYear = (_, value) => {
        setState((prevState) => ({
            ...prevState,
            payPeriodYear: value,
        }));
    };

    const onChangePayPeriod = (index) => {
        setState((prevState) => ({
            ...prevState,
            payPeriodSelected: index,
        }));
    };

    return (
        <>
            <Grid item className={classes.left}>
                <>
                    <Select
                        size="sm"
                        name="payPeriodYear"
                        value={payPeriodYear}
                        onChange={onChangeYear}
                        placeholder="Select Year"
                        className={classes.yearDropdown}
                        options={PayrollUtils.getYears()}
                    />
                    <List className={classes.payPeriodList} component="nav">
                        { payPeriods.map((item, index) => (
                            <ListItem
                                button
                                key={index}
                                className={classes.listItemStyle}
                                selected={index === payPeriodSelected}
                                onClick={() => onChangePayPeriod(index)}
                                classes={{ root: classes.listItemRoot, selected: classes.selected }}
                            >
                                <ListItemText primary={item} />
                            </ListItem>
                        ))}
                    </List>
                </>
            </Grid>
            <List className={classes.listStyle} component="nav">
                { REPORTS.map((item, index) => (
                    <ListItem
                        button
                        key={index}
                        disabled={printing}
                        className={classes.listItemStyle}
                        selected={index === reportSelected}
                        onClick={() => onChangeReport(index, item.value)}
                        classes={{ root: classes.listItemRoot, selected: classes.selected }}
                    >
                        <ListItemText primary={item.label} />
                    </ListItem>
                ))}
            </List>
            {openNachaDialog && (
                <NachaEffectiveDate
                    onCloseDialog={toggleNachaEffectiveDateDialog}
                />
            )}
            {openRT6FormDialog && (
                <RT6Form
                    onCloseDialog={toggleRT6FormDialog}
                />
            )}
        </>
    );
};
export default Reports;
